import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Plus, Pencil, Trash2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { listBlogAdmin, createBlog, updateBlog, deleteBlog } from "../api";
import { PageHeader } from "./Dashboard";
import ImageUploader from "../components/ImageUploader";
import RefreshButton from "../components/RefreshButton";

const EMPTY = {
  slug: "",
  title: "",
  category: "Style Guides",
  excerpt: "",
  cover: "",
  author: "Asvasa Studio",
  read_minutes: 5,
  published_at: new Date().toISOString().slice(0, 10),
  tags: [],
  content: [""],
};

export function BlogList() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = () => {
    setLoading(true);
    listBlogAdmin().then(setPosts).finally(() => setLoading(false));
  };
  useEffect(reload, []);

  const onDelete = async (slug) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await deleteBlog(slug);
      toast.success("Post deleted");
      reload();
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <div data-testid="admin-blog-list">
      <PageHeader eyebrow="Editorial" title="Journal" sub={`${posts.length} stories published`} />
      <div className="p-10">
        <div className="flex items-center justify-between mb-6">
          <RefreshButton onClick={reload} loading={loading} testid="admin-blog-refresh" />
          <Link
            to="/admin/blog/new"
            className="bg-[#c3ba9e] text-[#0d0d0e] px-5 py-3 text-[11px] tracking-[0.28em] uppercase font-medium flex items-center gap-2 hover:bg-[#FDFAF6] transition-colors"
            data-testid="admin-add-blog"
          >
            <Plus size={14} /> Write a story
          </Link>
        </div>

        <div className="bg-[#161617] border border-[#2a2a2d]">
          <div className="grid grid-cols-[80px_2fr_1fr_140px_120px] gap-4 px-6 py-4 border-b border-[#2a2a2d] text-[10px] tracking-[0.28em] uppercase text-[#8c8a7e]">
            <span>Cover</span>
            <span>Title</span>
            <span>Category</span>
            <span>Published</span>
            <span></span>
          </div>
          {loading ? (
            <p className="p-10 text-center text-[#8c8a7e] text-sm">Loading…</p>
          ) : posts.length === 0 ? (
            <p className="p-10 text-center text-[#8c8a7e] text-sm">No posts yet.</p>
          ) : (
            posts.map((p) => (
              <div
                key={p.slug}
                className="grid grid-cols-[80px_2fr_1fr_140px_120px] gap-4 px-6 py-4 border-b border-[#2a2a2d] items-center hover:bg-[#1a1a1c] transition-colors"
                data-testid={`admin-blog-row-${p.slug}`}
              >
                <img src={p.cover} alt={p.title} className="w-14 h-14 object-cover bg-[#1f1f21]" />
                <div>
                  <p className="text-[#e9e6db] text-sm leading-tight">{p.title}</p>
                  <p className="text-[11px] text-[#5e5d54] mt-0.5">{p.author} · {p.read_minutes} min</p>
                </div>
                <span className="text-[12px] text-[#9a9787]">{p.category}</span>
                <span className="text-[12px] text-[#9a9787]">{p.published_at}</span>
                <div className="flex justify-end gap-2">
                  <Link to={`/admin/blog/${p.slug}/edit`} className="p-2 text-[#9a9787] hover:text-[#c3ba9e]" data-testid={`admin-blog-edit-${p.slug}`}>
                    <Pencil size={14} />
                  </Link>
                  <button onClick={() => onDelete(p.slug)} className="p-2 text-[#9a9787] hover:text-[#c97070]" data-testid={`admin-blog-delete-${p.slug}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export function BlogForm() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (slug && slug !== "new") {
      listBlogAdmin().then((all) => {
        const p = all.find((x) => x.slug === slug);
        if (p) setData(p);
      });
    }
  }, [slug]);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...data,
        read_minutes: Number(data.read_minutes),
        tags: typeof data.tags === "string" ? data.tags.split(",").map((s) => s.trim()).filter(Boolean) : data.tags,
        content: (typeof data.content === "string"
          ? data.content.split("\n\n").map((s) => s.trim())
          : data.content
        ).filter(Boolean),
      };
      if (slug && slug !== "new") {
        await updateBlog(slug, payload);
        toast.success("Post updated");
      } else {
        await createBlog(payload);
        toast.success("Post published");
      }
      navigate("/admin/blog");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const tagsText = Array.isArray(data.tags) ? data.tags.join(", ") : data.tags;
  const contentText = Array.isArray(data.content) ? data.content.join("\n\n") : data.content;

  return (
    <div data-testid="admin-blog-form">
      <PageHeader eyebrow={slug && slug !== "new" ? "Edit" : "New"} title={data.title || "Untitled story"} />
      <div className="p-10 max-w-3xl">
        <Link to="/admin/blog" className="inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-[#8c8a7e] hover:text-[#c3ba9e] mb-8">
          <ArrowLeft size={14} /> Back to blog
        </Link>

        <form onSubmit={submit} className="space-y-6">
          <Field label="Slug" required>
            <input value={data.slug} onChange={(e) => set("slug", e.target.value)} disabled={slug && slug !== "new"} required data-testid="admin-blog-slug" className={input} />
          </Field>
          <Field label="Title" required>
            <input value={data.title} onChange={(e) => set("title", e.target.value)} required data-testid="admin-blog-title" className={input} />
          </Field>
          <div className="grid grid-cols-2 gap-5">
            <Field label="Category">
              <select value={data.category} onChange={(e) => set("category", e.target.value)} className={input}>
                <option>Philosophy</option>
                <option>Style Guides</option>
                <option>Fabric & Craftsmanship</option>
                <option>Festive Wear</option>
              </select>
            </Field>
            <Field label="Author">
              <input value={data.author} onChange={(e) => set("author", e.target.value)} className={input} />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-5">
            <Field label="Published date">
              <input type="date" value={data.published_at} onChange={(e) => set("published_at", e.target.value)} className={input} />
            </Field>
            <Field label="Read time (min)">
              <input type="number" value={data.read_minutes} onChange={(e) => set("read_minutes", e.target.value)} className={input} />
            </Field>
          </div>
          <Field label="Cover image">
            <ImageUploader
              value={data.cover ? [data.cover] : []}
              onChange={(urls) => set("cover", urls[0] || "")}
              folder="asvasa/blog"
              max={1}
              testid="blog-cover-uploader"
              guidelines="1600 × 1000 px (16:10 landscape) · JPG/PNG/WEBP · under 3 MB · keep editorial focus left or centred"
            />
          </Field>
          <Field label="Excerpt">
            <textarea rows={3} value={data.excerpt} onChange={(e) => set("excerpt", e.target.value)} className={`${input} resize-none`} />
          </Field>
          <Field label="Tags (comma-separated)">
            <input value={tagsText} onChange={(e) => set("tags", e.target.value)} className={input} />
          </Field>
          <Field label="Content (separate paragraphs with a blank line)">
            <textarea rows={14} value={contentText} onChange={(e) => set("content", e.target.value)} className={`${input} resize-none`} />
          </Field>

          <div className="flex gap-3 pt-4">
            <button type="submit" disabled={saving} className="bg-[#c3ba9e] text-[#0d0d0e] px-8 py-3 text-[11px] tracking-[0.28em] uppercase font-medium hover:bg-[#FDFAF6] disabled:opacity-50" data-testid="admin-blog-save">
              {saving ? "Publishing…" : "Save & publish"}
            </button>
            <Link to="/admin/blog" className="border border-[#2a2a2d] text-[#9a9787] px-8 py-3 text-[11px] tracking-[0.28em] uppercase hover:bg-[#1f1f21]">
              Cancel
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}

const input = "w-full bg-[#0d0d0e] border border-[#2a2a2d] focus:border-[#c3ba9e] outline-none px-3 py-2.5 text-[14px] text-[#e9e6db]";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-[10px] tracking-[0.28em] uppercase text-[#8c8a7e] mb-2 block">
        {label}
        {required && <span className="text-[#c79a3a] ml-1">*</span>}
      </span>
      {children}
    </label>
  );
}
